import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { useState } from "react";

import {
  LiveViewAreasOfInterestTab,
  type LiveViewAreasOfInterestTabProps,
} from "@/features/deployment-dashboard/live-view/live-view-areas-of-interest-tab";
import {
  LiveViewQueuesTab,
  type LiveViewQueue,
} from "@/features/deployment-dashboard/live-view/live-view-queues-tab";
import type { LiveViewStats } from "@/features/deployment-dashboard/live-view/utils/derive-live-view-stats";
import type { AreaCurrentPopulationState } from "@/features/deployment-dashboard/shared/hooks/use-area-current-population";
import { calculateDerivedPopulation } from "@/features/deployment-dashboard/shared/population-metrics";
import type { LiveStreamStatus } from "@/features/deployment-dashboard/live-view/hooks/use-live-tracks-stream";

const PANEL_WIDTH = 300;
export type LiveViewSidebarTab = "areasOfInterest" | "queues";

interface SummaryMetricProps {
  label: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
}

function SummaryMetric({ label, children, icon }: SummaryMetricProps) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", fontWeight: 700, textTransform: "uppercase" }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5, minHeight: 24 }}>
        {icon}
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {children}
        </Typography>
      </Box>
    </Box>
  );
}

export interface LiveViewSidePanelProps extends LiveViewAreasOfInterestTabProps {
  title: string;
  stats: LiveViewStats;
  areaCurrentPopulation: AreaCurrentPopulationState;
  liveTracksStatus?: LiveStreamStatus;
  liveTracksHasFrame?: boolean;
  queues?: LiveViewQueue[];
  selectedQueueId?: string | null;
  onToggleQueue?: (queueId: string) => void;
  queuesLoading?: boolean;
  queuesError?: Error | null;
  activeTab?: LiveViewSidebarTab;
  onActiveTabChange?: (tab: LiveViewSidebarTab) => void;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}

export function LiveViewSidePanel({
  title,
  stats,
  areaCurrentPopulation,
  liveTracksStatus = "disabled",
  liveTracksHasFrame = false,
  queues = [],
  selectedQueueId = null,
  onToggleQueue = () => undefined,
  queuesLoading = false,
  queuesError = null,
  activeTab,
  onActiveTabChange,
  expanded,
  onExpandedChange,
  securityLaneStatuses = {},
  ...areasOfInterestTabProps
}: LiveViewSidePanelProps) {
  const [uncontrolledActiveTab, setUncontrolledActiveTab] =
    useState<LiveViewSidebarTab>("areasOfInterest");
  const resolvedActiveTab = activeTab ?? uncontrolledActiveTab;
  const handleActiveTabChange = (tab: LiveViewSidebarTab) => {
    if (activeTab === undefined) setUncontrolledActiveTab(tab);
    onActiveTabChange?.(tab);
  };
  const densityText =
    stats.averageDensity === null ? "No data" : `${stats.averageDensity.toFixed(2)} ppl/m²`;
  const hasTotalPopulation =
    !areaCurrentPopulation.isLoading &&
    areaCurrentPopulation.population !== null &&
    areaCurrentPopulation.dataStatus !== "unavailable";
  const totalPopulation =
    hasTotalPopulation && areaCurrentPopulation.population !== null
      ? areaCurrentPopulation.population
      : null;
  const totalPopulationText =
    totalPopulation !== null ? `${totalPopulation} people` : "Unavailable";
  const totalPopulationIsStale = areaCurrentPopulation.dataStatus === "stale";
  const selectedArea = areasOfInterestTabProps.areas.find(
    (area) => area.id === areasOfInterestTabProps.displayedAreaId
  );
  const derivedPopulation = calculateDerivedPopulation(selectedArea?.derivedPopulationMetrics, {
    totalPopulation,
    departuresLoungePopulation: liveTracksHasFrame ? stats.population : null,
  });
  const hasDerivedPopulation = selectedArea?.derivedPopulationMetrics?.duty_free_population != null;
  const derivedPopulationUnavailable =
    liveTracksStatus === "auth-failed" ||
    liveTracksStatus === "error" ||
    liveTracksStatus === "offline" ||
    !liveTracksHasFrame;
  const derivedPopulationIsStale = areaCurrentPopulation.dataStatus === "stale";

  return (
    <Box
      sx={{
        position: "relative",
        width: expanded ? PANEL_WIDTH : 0,
        flexShrink: 0,
        transition: (theme) => theme.transitions.create("width"),
        zIndex: 2,
      }}
    >
      <Paper
        component="aside"
        aria-label="Live occupancy summary"
        square
        elevation={0}
        sx={{
          width: PANEL_WIDTH,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: 1,
          borderColor: "divider",
          opacity: expanded ? 1 : 0,
          visibility: expanded ? "visible" : "hidden",
          transition: (theme) =>
            theme.transitions.create(["opacity", "visibility"], {
              duration: theme.transitions.duration.shortest,
            }),
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" noWrap>
            {title}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 2,
              mt: 2,
            }}
          >
            <SummaryMetric
              label="Population"
              icon={<GroupsOutlinedIcon sx={{ fontSize: "1.1rem", color: "primary.main" }} />}
            >
              {stats.population} people
            </SummaryMetric>
            {hasTotalPopulation && (
              <SummaryMetric label="Total Population">
                <Box
                  component="span"
                  sx={totalPopulationIsStale ? { opacity: 0.6, fontStyle: "italic" } : undefined}
                >
                  {totalPopulationText}
                </Box>
              </SummaryMetric>
            )}
            {hasDerivedPopulation && (
              <SummaryMetric label="Duty Free Population">
                <Box
                  component="span"
                  sx={
                    derivedPopulationUnavailable || derivedPopulationIsStale
                      ? { opacity: 0.6, fontStyle: "italic" }
                      : undefined
                  }
                >
                  {derivedPopulation == null ? "Unavailable" : `${derivedPopulation} people`}
                </Box>
              </SummaryMetric>
            )}
            <SummaryMetric label="Avg. Density">{densityText}</SummaryMetric>
          </Box>
        </Box>
        <Divider />
        <Tabs
          value={resolvedActiveTab}
          onChange={(_, value: LiveViewSidebarTab) => handleActiveTabChange(value)}
          variant="fullWidth"
          aria-label="Live view detail tabs"
        >
          <Tab
            value="areasOfInterest"
            label="Areas of Interest"
            id="live-view-tab-areas-of-interest"
            aria-controls="live-view-tabpanel-areas-of-interest"
          />
          <Tab
            value="queues"
            label="Queues"
            id="live-view-tab-queues"
            aria-controls="live-view-tabpanel-queues"
          />
        </Tabs>
        {resolvedActiveTab === "areasOfInterest" ? (
          <Box
            role="tabpanel"
            id="live-view-tabpanel-areas-of-interest"
            aria-labelledby="live-view-tab-areas-of-interest"
            sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
          >
            <LiveViewAreasOfInterestTab
              {...areasOfInterestTabProps}
              liveTracksHasFrame={liveTracksHasFrame}
              securityLaneStatuses={securityLaneStatuses}
            />
          </Box>
        ) : (
          <Box
            role="tabpanel"
            id="live-view-tabpanel-queues"
            aria-labelledby="live-view-tab-queues"
            sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
          >
            <LiveViewQueuesTab
              queues={queues}
              selectedQueueId={selectedQueueId}
              onToggleQueue={onToggleQueue}
              isLoading={queuesLoading}
              error={queuesError}
            />
          </Box>
        )}
      </Paper>

      <IconButton
        aria-label={expanded ? "Collapse occupancy summary" : "Expand occupancy summary"}
        onClick={() => onExpandedChange(!expanded)}
        size="small"
        sx={{
          position: "absolute",
          top: "50%",
          right: -16,
          transform: "translateY(-50%)",
          width: 32,
          height: 48,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          backgroundColor: "background.paper",
          boxShadow: 2,
          "&:hover": { backgroundColor: "action.hover" },
        }}
      >
        {expanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
      </IconButton>
    </Box>
  );
}
